import { FormEvent, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dismissContentReport, fetchContentReports, resolveContentReport } from '../api/adminContentReports';
import type { ReportContentReport } from '../api/types';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';
import { formatDateTime, formatNumber } from '../utils/format';

const TABS = ['open', 'closed'] as const;
type TabKey = typeof TABS[number];
type ModerationAction = 'resolve' | 'markInvalid' | 'hide' | 'restore' | 'dismiss';

function reportScope(report: ReportContentReport['report']) {
  return report.guild ? `<${report.guild.name}>` : 'Personal log';
}

function statusBadge(report: ReportContentReport['report']) {
  if (report.deletedAt) return <span className="badge text-bg-secondary">Deleted</span>;
  if (report.isInvalid) return <span className="badge text-bg-danger">Invalid</span>;
  if (report.isHidden) return <span className="badge text-bg-warning">Hidden</span>;
  return <span className="badge text-bg-success">Visible</span>;
}

function ContentReportCard({ report }: { report: ReportContentReport }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState(report.moderatorNote ?? '');
  const [activeAction, setActiveAction] = useState<ModerationAction | null>(null);

  const resolveMutation = useMutation({
    mutationFn: (action: Exclude<ModerationAction, 'dismiss'>) => resolveContentReport(report.id, { action, moderatorNote: note.trim() || undefined }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'content-reports'] }),
    onSettled: () => setActiveAction(null),
  });

  const dismissMutation = useMutation({
    mutationFn: () => dismissContentReport(report.id, note.trim() || undefined),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'content-reports'] }),
    onSettled: () => setActiveAction(null),
  });

  const pending = resolveMutation.isPending || dismissMutation.isPending;

  function submitAction(event: FormEvent, action: ModerationAction) {
    event.preventDefault();
    setActiveAction(action);
    if (action === 'dismiss') {
      dismissMutation.mutate();
    } else {
      resolveMutation.mutate(action);
    }
  }

  const error = resolveMutation.error ?? dismissMutation.error;

  return (
    <div className="card surface-card shadow-sm">
      <div className="card-body p-4">
        <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
          <div>
            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
              <h2 className="h5 mb-0">{report.report.displayName}</h2>
              {statusBadge(report.report)}
              <span className={`badge ${report.status === 'OPEN' ? 'text-bg-primary' : 'text-bg-secondary'}`}>{report.status}</span>
            </div>
            <div className="small text-body-secondary">
              {reportScope(report.report)} · {report.report.server?.name ?? 'Unknown server'} · Reported {formatDateTime(report.createdAt)} by {report.reporter.username}
            </div>
            <div className="mt-3">
              <div className="small text-body-secondary text-uppercase fw-semibold">Reported reason</div>
              <p className="mb-0">{report.reason || 'No reason provided.'}</p>
            </div>
          </div>
          <div className="d-flex flex-column align-items-start align-items-lg-end gap-2">
            <Link className="btn btn-sm btn-outline-light" to={`/reports/${report.report.id}`}>Open log report</Link>
            <div className="small text-body-secondary text-lg-end">
              {formatNumber(report.report.encounterCount)} encounters · {formatNumber(report.report.submissionCount)} POVs
            </div>
          </div>
        </div>

        {report.status === 'OPEN' ? (
          <form className="mt-4" onSubmit={(event) => submitAction(event, 'resolve')}>
            <label className="form-label small text-body-secondary" htmlFor={`note-${report.id}`}>Moderator note</label>
            <textarea
              id={`note-${report.id}`}
              className="form-control"
              rows={2}
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
            {error ? <div className="text-danger small mt-2">{error.message}</div> : null}
            <div className="d-flex flex-wrap gap-2 mt-3">
              <button className="btn btn-sm btn-outline-light" type="submit" disabled={pending && activeAction === 'resolve'}>Resolve</button>
              <button className="btn btn-sm btn-outline-warning" type="button" disabled={pending} onClick={(event) => submitAction(event as unknown as FormEvent, 'hide')}>Hide report</button>
              <button className="btn btn-sm btn-outline-danger" type="button" disabled={pending} onClick={(event) => submitAction(event as unknown as FormEvent, 'markInvalid')}>Mark invalid</button>
              <button className="btn btn-sm btn-outline-success" type="button" disabled={pending} onClick={(event) => submitAction(event as unknown as FormEvent, 'restore')}>Restore</button>
              <button className="btn btn-sm btn-outline-secondary" type="button" disabled={pending} onClick={(event) => submitAction(event as unknown as FormEvent, 'dismiss')}>Dismiss</button>
            </div>
          </form>
        ) : (
          <div className="mt-4 small text-body-secondary">
            Closed {report.resolvedAt ? formatDateTime(report.resolvedAt) : '—'} by {report.resolvedBy?.username ?? '—'}
            {report.moderatorNote ? <div className="mt-2"><span className="fw-semibold">Moderator note:</span> {report.moderatorNote}</div> : null}
          </div>
        )}
      </div>
    </div>
  );
}

export function AdminContentReportsPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('open');
  const query = useQuery({ queryKey: ['admin', 'content-reports'], queryFn: fetchContentReports });
  const reports = query.data ?? [];
  const openReports = useMemo(() => reports.filter((report) => report.status === 'OPEN'), [reports]);
  const closedReports = useMemo(() => reports.filter((report) => report.status !== 'OPEN'), [reports]);
  const visibleReports = activeTab === 'open' ? openReports : closedReports;

  return (
    <div className="content-wrapper page-main">
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-4">
        <div>
          <h1 className="display-6 fw-semibold mb-1">Log Report Moderation</h1>
          <p className="text-body-secondary mb-0">Review user reports for uploaded log reports.</p>
        </div>
        <div className="d-flex gap-2">
          {TABS.map((tab) => (
            <button key={tab} className={`btn btn-sm ${activeTab === tab ? 'btn-primary' : 'btn-outline-light'}`} type="button" onClick={() => setActiveTab(tab)}>
              {tab === 'open' ? `Open (${openReports.length})` : `Closed (${closedReports.length})`}
            </button>
          ))}
        </div>
      </div>

      {query.isLoading ? <LoadingState label="Loading content reports" /> : null}
      {query.isError ? <ErrorState title="Could not load content reports" error={query.error} /> : null}

      {!query.isLoading && !query.isError ? (
        <div className="d-flex flex-column gap-3">
          {visibleReports.length === 0 ? (
            <div className="card surface-card"><div className="card-body text-body-secondary">{activeTab === 'open' ? 'No open log report reports.' : 'No closed log report reports yet.'}</div></div>
          ) : visibleReports.map((report) => <ContentReportCard key={report.id} report={report} />)}
        </div>
      ) : null}
    </div>
  );
}
